import { Component, ElementRef, OnDestroy, ViewChild, ViewChildren, QueryList, EventEmitter, Output, Input } from '@angular/core';

import {
    forkJoin,
    map,
    Observable,
    filter,
    Subscription,
    switchMap,
    tap,
    BehaviorSubject,
    catchError,
    of,
    throwError
} from 'rxjs';

import { cloneDeep } from 'lodash';

import { DropdownOptionModel } from '../../../../Common/Forms/models/dropdown-option.model';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { ButtonTypesEnum } from '../../../../Common/Forms/enums/button-types.enum';
import { PreferencesModel } from '../../../../Core/Users/models/preferences.model';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';
import { MessageService } from 'primeng/api';

import { messageSeverityEnum } from '../../../../Common/Misc/enums/message-severity.enum';
import { CurationAgentsApiService } from '../../../../api/agents/curation-agents.api.service';
import { UserPreferencesService } from '../../services/user-preferences.service';
import { ConfirmDialogModel } from '../../../../Common/Dialogs/models/confirm-dialog.model';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { ConfirmDialogComponent } from '../../../../Common/Dialogs/components/ConfirmDialog/confirm-dialog.component';
import type { SectionListItems } from '../UserPreferences/user-preferences.component';
import { CurrencyConversionService } from '../../../../Core/Users/services/currency-conversion.service';
import { AccountModel } from '../../../../Core/Users/models/account.model';
import { MfaService } from '../../../../Bootstrap/services/mfa.service';
import { dateFormats } from '../../../../Core/Users/data/date-formats';
import { timeFormatsEnum } from '../../../../Core/Users/enums/time-formats.enum';
import { NotificationPreferenceTypes } from '../../../../Core/Users/enums/notification-preference-types.enum';
import { userPermissionsEnum } from '../../../../Core/Users/enums/user-permissions.enum';
import { UserAppGeneralPreferencesModel } from '../../../../Core/Users/models/user-app-preferences.model';
import { FavoriteColorsService, FAVORITE_COLORS_SECTION_ID } from '@Common/Forms/components/AdvancedColorPickerPopover/service/favorite-colors.service';

@Component({
    selector: 'agpr-general-preferences',
    templateUrl: './general-preferences.component.html',
    styleUrls: ['./general-preferences.component.scss'],
    standalone: false
})
export class GeneralPreferencesComponent implements OnDestroy {
    readonly buttonTypes = ButtonTypesEnum;
    readonly notificationPreferenceTypes = NotificationPreferenceTypes;

    @Input()
    set generalPrefs(value: UserAppGeneralPreferencesModel) {
        this.collapseToolbars = value.accessibility.collapseToolbars;
        this.tooltipsOnFocus = value.accessibility.tooltipsOnFocus;
    }

    @Output() sectionListItems = new EventEmitter<SectionListItems>();
    @Output() childErrors = new EventEmitter<ElementRef[]>();
    @Output() removeErrors = new EventEmitter<ElementRef[]>();
    @Output() checkDirty = new EventEmitter<void>();

    dateFormatOptions: DropdownOptionModel[] = [];
    selectedDateFormat: DropdownOptionModel;
    twentyFourHourTimeSelected: boolean;
    uiLanguageOptions: DropdownOptionModel[] = [];
    referenceDataLanguageOptions: DropdownOptionModel[] = [];
    selectedUiLanguage: DropdownOptionModel;
    selectedReferenceDataLanguage: DropdownOptionModel;
    generalPreferences: PreferencesModel;
    subscriptions: Subscription[] = [];

    currencyOptions: DropdownOptionModel[] = [];
    selectedCurrencyOption: DropdownOptionModel;

    hasMfaAuths: boolean = false;
    selectedAccount: AccountModel = new AccountModel();

    collapseToolbars: boolean;
    tooltipsOnFocus: boolean;
    favoriteColors: string[] = [];

    suggestions$: BehaviorSubject<DropdownOptionModel[]> = new BehaviorSubject<DropdownOptionModel[]>([]);

    subscriptionModerationWarnings: boolean;
    selectedSubscriptionModerationWarnings: NotificationPreferenceTypes = NotificationPreferenceTypes.ALL;

    socialModerationWarnings: boolean;
    selectedSocialModerationWarnings: NotificationPreferenceTypes = NotificationPreferenceTypes.ALL;

    alertExpirationWarnings: boolean;
    selectedAlertExpirationWarnings: NotificationPreferenceTypes = NotificationPreferenceTypes.ALL;

    topicModerationWarnings: boolean;
    selectedTopicModerationWarnings: NotificationPreferenceTypes = NotificationPreferenceTypes.ALL;

    hasMonitoring: boolean = false;
    hasTargeting: boolean = false;
    hasSocial: boolean = false;
    hasAutomationAgents: boolean = false;

    /**
     * Quota threshold warnings for automation agents. Kept apart from the preferences record - it
     * belongs to agents-service, which stores it per user against its own endpoint.
     */
    automationAgentQuotaWarnings: boolean = true;
    private savedAutomationAgentQuotaWarnings: boolean = true;
    readonly favoriteColorId = FAVORITE_COLORS_SECTION_ID;

    @ViewChild('favoriteColorsSection', { read: ElementRef }) favoriteColorsSection!: ElementRef;
    @ViewChildren('section') set sections(list: QueryList<ElementRef>) {
        if (list) {
            this.sectionListItems.emit(
                list.toArray().map(item => ({
                    label: `UserPreferences.general.${item.nativeElement.id}`,
                    value: item
                }))
            );
        }
    };

    constructor(
        private localizationService: LocalizationService,
        private currentUserStateService: CurrentUserStateService,
        private userPreferencesService: UserPreferencesService,
        private coreDialogService: CoreDialogService,
        private currencyConversionService: CurrencyConversionService,
        private mfaService: MfaService,
        private favoriteColorsService: FavoriteColorsService,
        private curationAgentsApi: CurationAgentsApiService,
        private messageService: MessageService
    ) {
        this.subscriptions.push(
            this.currentUserStateService.getPreferences().pipe(
                filter(x => !!x),
                tap(prefs => {
                    this.generalPreferences = prefs;
                    if (prefs.notificationPreference) {
                        this.generalPreferences.notificationPreference = cloneDeep(prefs.notificationPreference);
                    }
                    this.dateFormatOptions = dateFormats.map(format => new DropdownOptionModel(this.localizationService.transform(format.name), format.id));
                    this.selectedDateFormat = this.dateFormatOptions.find(option => option.value === this.generalPreferences.dateFormatId);
                    this.twentyFourHourTimeSelected = this.generalPreferences.timeFormat === timeFormatsEnum.twentyFourHour;
                    this.prepareUserCurrency();
                    this.prepareNotificationPreference();
                }),
                switchMap(() => forkJoin([
                    this.getUILanguages(),
                    this.getReferenceDataLanguages(),
                ]))
            ).subscribe(),
            this.currentUserStateService.getAccount().pipe(
                filter(x => !!x)
            ).subscribe(account => {
                this.selectedAccount = account;
            }),
            this.currentUserStateService.getPermissions().subscribe(permissions => {
                if (permissions) {
                    this.hasTargeting = permissions.includes(userPermissionsEnum.advancedTargeting);
                    this.hasMonitoring = permissions.includes(userPermissionsEnum.advancedMonitoring);
                    this.hasSocial = permissions.includes(userPermissionsEnum.socialIntelligence);
                    this.hasAutomationAgents = permissions.includes(userPermissionsEnum.agenticFeature);

                    if (this.hasAutomationAgents) {
                        this.loadAutomationAgentQuotaWarnings();
                    }
                }
            }),
            this.mfaService.all$.subscribe(
                x => this.hasMfaAuths = x.length > 0
            ),
            // Respect saved colors: favorite color may vary across platforms and is stored instantly on the server.
            this.favoriteColorsService.getFavoriteColors$().subscribe((colors: string[]) => {
                this.favoriteColors = colors;
            })
        );
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
        this.removeErrors.emit([this.favoriteColorsSection]);
    }

    uiLanguageChanged(event: DropdownOptionModel) {
        this.generalPreferences.uiLanguage.id = event.value;

        if (this.generalPreferences.uiLanguage.id === this.generalPreferences.referenceDataLanguage.id) {
            return;
        }

        const confirmDialogModel: ConfirmDialogModel = {
            inputs: {
                heading: 'UserPreferences.updateRefDataLanguageDialog.heading',
                message: this.localizationService.transform(
                    'UserPreferences.updateRefDataLanguageDialog.message', { selectedLanguage: this.selectedUiLanguage.label }
                ),
                showCancel: true,
                okLabel: 'UserPreferences.updateRefDataLanguageDialog.update',
                cancelLabel: 'UserPreferences.updateRefDataLanguageDialog.noThanks'
            },
            outputs: {
                dialogClose: (confirmed: boolean) => {
                    if (confirmed) {
                        this.referenceDataLanguageChanged(event);
                    } else {
                        this.checkDirty.emit();
                    }
                }
            }
        };
        this.coreDialogService.open(ConfirmDialogComponent, confirmDialogModel);
    }

    dateFormatChanged(event: DropdownOptionModel) {
        this.generalPreferences.dateFormatId = event.value;
        this.generalPreferences.dateFormat = dateFormats.find(format => format.id === event.value).value;
        this.checkDirty.emit();
    }

    timeFormatChanged(event: boolean) {
        this.generalPreferences.timeFormat = event ? timeFormatsEnum.twentyFourHour : timeFormatsEnum.twelveHour;
        this.checkDirty.emit();
    }

    referenceDataLanguageChanged(event) {
        this.generalPreferences.referenceDataLanguage.id = event.value;
        this.selectedReferenceDataLanguage = this.referenceDataLanguageOptions
            .find(option => option.value === this.generalPreferences?.referenceDataLanguage.id);
        this.checkDirty.emit();
    }

    timeZoneChanged(event) {
        this.generalPreferences.timeZone = event;
        this.checkDirty.emit();
    }

    private getUILanguages(): Observable<void> {
        return this.userPreferencesService.getUILanguages().pipe(
            tap(languages => {
                this.uiLanguageOptions = (languages ?? [])
                    .map(x => new DropdownOptionModel(this.localizationService.transform(`Language.${x.languageTag}`), x.id))
                    .sort((a, b) => a.label.localeCompare(b.label));
                this.selectedUiLanguage = this.uiLanguageOptions.find(option => option.value === this.generalPreferences?.uiLanguage.id);
            }),
            map(() => undefined)
        );
    }

    private getReferenceDataLanguages(): Observable<void> {
        return this.userPreferencesService.getReferenceDataLanguages().pipe(
            tap(languages => {
                this.referenceDataLanguageOptions = (languages ?? [])
                    .map(x => new DropdownOptionModel(this.localizationService.transform(`Language.${x.languageTag}`), x.id))
                    .sort((a, b) => a.label.localeCompare(b.label));
                this.selectedReferenceDataLanguage = this.referenceDataLanguageOptions
                    .find(option => option.value === this.generalPreferences?.referenceDataLanguage.id);
            }),
            map(() => undefined)
        );
    }

    private prepareUserCurrency() {
        this.currencyOptions = this.currencyConversionService.getCurrencyDropdownOptions();
        this.suggestions$.next(this.currencyOptions);
        const userCurrency = this.generalPreferences?.currency;
        this.selectedCurrencyOption = this.currencyOptions.find(option => option.value === userCurrency);
    }

    selectCurrency(event: DropdownOptionModel) {
        this.generalPreferences.currency = event.value;
        this.checkDirty.emit();
    }

    getUpdatedValues(): PreferencesModel {
        return this.generalPreferences;
    }

    prepareNotificationPreference(): void {
        this.subscriptionModerationWarnings
            = this.generalPreferences.notificationPreference.subscriptionModerationWarnings !== NotificationPreferenceTypes.NONE;
        this.socialModerationWarnings = this.generalPreferences.notificationPreference.socialModerationWarnings !== NotificationPreferenceTypes.NONE;
        this.alertExpirationWarnings = this.generalPreferences.notificationPreference.alertExpirationWarnings !== NotificationPreferenceTypes.NONE;
        this.topicModerationWarnings = this.generalPreferences.notificationPreference.topicModerationWarnings !== NotificationPreferenceTypes.NONE;
        if (this.subscriptionModerationWarnings) {
            this.selectedSubscriptionModerationWarnings = this.generalPreferences.notificationPreference.subscriptionModerationWarnings;
        }

        if (this.socialModerationWarnings) {
            this.selectedSocialModerationWarnings = this.generalPreferences.notificationPreference.socialModerationWarnings;
        }

        if (this.topicModerationWarnings) {
            this.selectedTopicModerationWarnings = this.generalPreferences.notificationPreference.topicModerationWarnings;
        }
    }

    /** Whether the box has been moved since it was read, which the preferences diff cannot tell */
    get automationAgentQuotaWarningsDirty(): boolean {
        return this.hasAutomationAgents
            && this.automationAgentQuotaWarnings !== this.savedAutomationAgentQuotaWarnings;
    }

    /**
     * Writes the automation agent warning preference, which the preferences record does not carry.
     * Called by the page on save, and a no-op when the box has not been touched.
     */
    saveAutomationAgentQuotaWarnings(): Observable<void> {
        if (!this.automationAgentQuotaWarningsDirty) {
            return of(null);
        }

        return this.curationAgentsApi.setNotificationPreference(this.automationAgentQuotaWarnings).pipe(
            tap(preference => {
                this.savedAutomationAgentQuotaWarnings = preference?.enabled;
                // The preferences record's own save growls for itself; this one is saved apart from
                // it, so a change to it alone would otherwise go without a word
                this.messageService.add({
                    severity: messageSeverityEnum.success,
                    detail: 'UserPreferences.update.success.message',
                    summary: 'UserPreferences.update.success.heading'
                });
            }),
            catchError(error => {
                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    detail: 'UserPreferences.update.error.message',
                    summary: 'UserPreferences.update.error.heading'
                });

                return throwError(() => error);
            }),
            map(() => null as void)
        );
    }

    /** Enabled unless the user has opted out, which is also what the service answers by default */
    private loadAutomationAgentQuotaWarnings(): void {
        this.subscriptions.push(
            this.curationAgentsApi.getNotificationPreference().pipe(
                catchError(() => of({ enabled: true }))
            ).subscribe(preference => {
                this.automationAgentQuotaWarnings = preference?.enabled ?? true;
                this.savedAutomationAgentQuotaWarnings = this.automationAgentQuotaWarnings;
            })
        );
    }

    notificationChanged(): void {
        if (!this.subscriptionModerationWarnings) {
            this.generalPreferences.notificationPreference.subscriptionModerationWarnings = NotificationPreferenceTypes.NONE;
        } else {
            this.generalPreferences.notificationPreference.subscriptionModerationWarnings = this.selectedSubscriptionModerationWarnings;
        }

        if (!this.socialModerationWarnings) {
            this.generalPreferences.notificationPreference.socialModerationWarnings = NotificationPreferenceTypes.NONE;
        } else {
            this.generalPreferences.notificationPreference.socialModerationWarnings = this.selectedSocialModerationWarnings;
        }

        if (!this.alertExpirationWarnings) {
            this.generalPreferences.notificationPreference.alertExpirationWarnings = NotificationPreferenceTypes.NONE;
        } else {
            this.generalPreferences.notificationPreference.alertExpirationWarnings = this.selectedAlertExpirationWarnings;
        }

        if (!this.topicModerationWarnings) {
            this.generalPreferences.notificationPreference.topicModerationWarnings = NotificationPreferenceTypes.NONE;
        } else {
            this.generalPreferences.notificationPreference.topicModerationWarnings = this.selectedTopicModerationWarnings;
        }

        this.checkDirty.emit();
    }

    onAccessibilityChanged() {
        this.checkDirty.emit();
    }


    onfavoriteColorsChanged(colors: string[]) {
        this.favoriteColors = colors;
        const uniqueColors = Array.from(new Set(colors));
        if (this.favoriteColors.length !== uniqueColors.length) {
            // Colors are duplicated
            this.childErrors.emit([this.favoriteColorsSection]);
        } else {
            this.removeErrors.emit([this.favoriteColorsSection]);
        }
        this.checkDirty.emit();
    }

    getUpdatedGeneralPrefs(): UserAppGeneralPreferencesModel {
        return {
            favoriteColors: this.favoriteColors,
            accessibility: {
                collapseToolbars: this.collapseToolbars,
                tooltipsOnFocus: this.tooltipsOnFocus
            }
        };
    }
}
